import React, { useEffect, useState } from 'react';
import { Chart as HChart } from 'highcharts';
import { Box, Skeleton, Stack, ToggleButton, ToggleButtonGroup } from '@mui/material';
import ChartCalendar from 'components/metric/ChartCalendar';
import { ChartingIntervalUnitType } from 'components/metric/chart';
import { MetricChartTools } from 'components/metric/chart/metricChartTools';

export interface IRangeOption {
    key: string;
    unit: ChartingIntervalUnitType | 'all' | 'custom';
    count: number;
    text: string;
}

const rangeOptions: IRangeOption[] = [
    { key: '1m', unit: 'month', count: 1, text: '1m' },
    { key: '3m', unit: 'month', count: 3, text: '3m' },
    { key: '6m', unit: 'month', count: 6, text: '6m' },
    { key: '1y', unit: 'year', count: 1, text: '1y' },
    { key: 'all', unit: 'all', count: 1, text: 'All' },
    { key: 'custom', unit: 'custom', count: 1, text: '' },
];
export default function TopControls({
    chart,
    selectedRange,
    onChange,
}: {
    chart?: HChart;
    selectedRange: string;
    onChange: (range: string) => void;
}) {
    const [isCalendarVisible, setIsCalendarVisible] = useState(false);

    const handleCalendarApply = (startDate: Date, endDate: Date) => {
        setIsCalendarVisible(false);
        if (!chart) {
            return;
        }
        chart.xAxis[0].setExtremes(startDate.getTime(), endDate.getTime());
        onChange('custom');
    };

    useEffect(() => {
        const range = rangeOptions.find((opt) => opt.key == selectedRange);
        if (!chart || !chart.xAxis?.[0] || !range || selectedRange == 'custom') {
            return;
        }

        const extremes = chart.xAxis[0].getExtremes();
        if (selectedRange == 'all') {
            chart.xAxis[0].setExtremes(extremes.dataMin, extremes.dataMax);
            return;
        }

        let min = extremes.max,
            max = extremes.max;

        const tmp = MetricChartTools.getRangeBorders(
            min,
            max,
            extremes.dataMin,
            range.unit as ChartingIntervalUnitType,
            range.count,
        );

        if (tmp && tmp.min) min = tmp.min;
        if (tmp && tmp.max) max = tmp.max;
        if (extremes.dataMin > min) min = extremes.dataMin;
        if (extremes.dataMax < tmp.max) max = extremes.dataMax;
        chart.xAxis[0].setExtremes(min - 1, max + 1);
    }, [selectedRange]);

    if (!chart) {
        return (
            <Box>
                <Skeleton variant="rectangular" width={'100%'} height={52} />
            </Box>
        );
    }

    return (
        <Stack
            direction="row"
            sx={{
                p: 2,
                pb: 1,
                position: 'relative',
                '@container chartContainer (width < 900px)': {
                    '&': {
                        mb: 1.5,
                    },
                },
            }}
        >
            <ToggleButtonGroup color="primary" value={selectedRange} exclusive onChange={(_, value) => onChange(value)}>
                {rangeOptions
                    .filter((opt) => opt.key != 'custom')
                    .map((opt) => (
                        <ToggleButton value={opt.key} key={opt.key}>
                            {opt.text}
                        </ToggleButton>
                    ))}
            </ToggleButtonGroup>

            <ChartCalendar
                chart={chart}
                hasTime={false}
                isCalendarVisible={isCalendarVisible}
                onOpen={() => setIsCalendarVisible(true)}
                onClose={() => setIsCalendarVisible(false)}
                onApply={handleCalendarApply}
            />
        </Stack>
    );
}
